import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  IconButton,
  TextField
} from '@mui/material';
import { DataGrid, GridCellParams, GridColumns } from '@mui/x-data-grid';
import {
  DeleteForeverRounded as DeleteIcon,
  GetAppRounded as ExportIcon,
  UploadFileRounded as ImportIcon,
  SearchRounded as SearchIcon,
  ClearRounded as ClearIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';

import {
  useSpot,
  useErrorNotification,
  downloadCsv,
  getApi,
  Brand,
  Type
} from '../../framework';
import {
  BrandSelector,
  CenteredRow,
  Container,
  FileInputButton,
  FormLabel,
  FormRow,
  FormSection,
  FormSectionTitle,
  Gap,
  Row,
  Spacer
} from '../../components';

interface SizeRemapItem {
  id: number;
  brand: string;
  productType: string;
  gender: 'male' | 'female' | 'unisex';
  heightCategory: string;
  weightCategory: string;
  age: number;
  currentSize: string;
  proposedSize: string;
  createdAt: Date;
  updatedAt: Date;
}

interface SizeRemapProps {
  onLoadingChanged: (loading: boolean) => void;
}

const DataGridFrame = styled(Box)`
  position: relative;
  height: calc(100vh - 300px);
  width: 100%;
  margin-top: ${p => p.theme.spacing(2)};
`;

const SearchContainer = styled(Box)`
  padding: ${p => p.theme.spacing(1)};
  flex: 1 1 auto;
`;

export function SizeRemap({ onLoadingChanged }: SizeRemapProps) {
  const { t } = useTranslation();
  const { query, raw, spot } = useSpot();
  const { displayErrors, displaySuccess, notification } =
    useErrorNotification();

  const [loading, setLoading] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [selectedProductType, setSelectedProductType] = useState<string>('');
  const [selectedGender, setSelectedGender] = useState<
    'male' | 'female' | 'unisex'
  >('male');
  const [productTypes, setProductTypes] = useState<Type[]>([]);
  const [sizeRemaps, setSizeRemaps] = useState<SizeRemapItem[]>([]);
  const [search, setSearch] = useState<string>('');
  const [confirmDelete, setConfirmDelete] = useState<{
    onConfirm: () => void;
  } | null>(null);

  const setIsLoading = useCallback(
    (isLoading: boolean) => {
      setLoading(isLoading);
      onLoadingChanged(isLoading);
    },
    [onLoadingChanged]
  );

  const loadProductTypes = useCallback(async () => {
    if (!selectedBrand) return;

    try {
      setIsLoading(true);
      const response = await raw<Type[]>(`type/`, {
        method: 'GET'
      });
      setProductTypes(response || []);
    } catch (e) {
      displayErrors(e as Error[]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedBrand, raw, displayErrors, setIsLoading]);

  const loadSizeRemaps = useCallback(async () => {
    if (!selectedBrand?.slug || !selectedProductType || !selectedGender) return;

    setSizeRemaps([]);

    try {
      setIsLoading(true);
      const response = await raw<SizeRemapItem[]>(
        `size-remap/${encodeURIComponent(selectedBrand.slug)}/${encodeURIComponent(selectedProductType)}/${selectedGender}`,
        { method: 'GET' }
      );
      setSizeRemaps(response || []);
    } catch (e) {
      displayErrors(e as Error[]);
    } finally {
      setIsLoading(false);
    }
  }, [
    selectedBrand,
    selectedProductType,
    selectedGender,
    raw,
    displayErrors,
    setIsLoading
  ]);

  const exportRemaps = useCallback(async () => {
    if (!selectedBrand?.slug || !selectedProductType || !selectedGender) return;

    try {
      setIsLoading(true);
      const response = await fetch(
        `${getApi()}/size-remap/${encodeURIComponent(selectedBrand.slug)}/${encodeURIComponent(selectedProductType)}/${selectedGender}/export`,
        {
          method: 'GET',
          credentials: 'include'
        }
      );
      const csv = await response.text();
      downloadCsv(
        `${selectedBrand.slug}-${selectedProductType}-${selectedGender}-size-remaps.csv`,
        csv
      );
      displaySuccess();
    } catch (e) {
      displayErrors(e as Error[]);
    } finally {
      setIsLoading(false);
    }
  }, [
    selectedBrand,
    selectedProductType,
    selectedGender,
    displayErrors,
    displaySuccess,
    setIsLoading
  ]);

  const handleImportCsv = useCallback(
    async (file: File) => {
      if (
        !selectedBrand?.slug ||
        !selectedProductType ||
        !selectedGender ||
        !file
      )
        return;

      try {
        setIsLoading(true);
        await raw(
          `size-remap/${encodeURIComponent(selectedBrand.slug)}/${encodeURIComponent(selectedProductType)}/${selectedGender}/csv`,
          {
            body: file,
            method: 'POST',
            headers: {
              'Content-Type': 'text/csv'
            }
          }
        );
        displaySuccess();
        await loadSizeRemaps();
      } catch (e) {
        displayErrors(e as Error[]);
      } finally {
        setIsLoading(false);
      }
    },
    [
      selectedBrand,
      selectedProductType,
      selectedGender,
      raw,
      displayErrors,
      displaySuccess,
      setIsLoading,
      loadSizeRemaps
    ]
  );

  const handleDeleteRemap = useCallback(
    (id: number) => {
      setConfirmDelete({
        onConfirm: async () => {
          try {
            setIsLoading(true);
            await raw(`size-remap/${id}`, {
              method: 'DELETE'
            });
            displaySuccess();
            await loadSizeRemaps();
          } catch (e) {
            displayErrors(e as Error[]);
          } finally {
            setIsLoading(false);
            setConfirmDelete(null);
          }
        }
      });
    },
    [raw, displayErrors, displaySuccess, setIsLoading, loadSizeRemaps]
  );

  const handleProductTypeChange = useCallback((event: SelectChangeEvent) => {
    setSelectedProductType(event.target.value);
    setSizeRemaps([]);
  }, []);

  const handleGenderChange = useCallback((event: SelectChangeEvent) => {
    setSelectedGender(event.target.value as 'male' | 'female' | 'unisex');
    setSizeRemaps([]);
  }, []);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.target.value);
    },
    [setSearch]
  );

  const handleBrandChange = useCallback(
    (brand: Brand | null) => {
      setSelectedBrand(brand);
      setSizeRemaps([]);
    },
    [setSelectedBrand, setSelectedProductType, setSizeRemaps]
  );

  useEffect(() => {
    loadProductTypes();
  }, [loadProductTypes]);

  useEffect(() => {
    if (selectedBrand && selectedProductType && selectedGender) {
      loadSizeRemaps();
    }
  }, [selectedBrand, selectedProductType, selectedGender, loadSizeRemaps]);

  const filteredRemaps = sizeRemaps.filter(remap => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      remap.heightCategory.toLowerCase().includes(searchLower) ||
      remap.weightCategory.toLowerCase().includes(searchLower) ||
      remap.currentSize.toLowerCase().includes(searchLower) ||
      remap.proposedSize.toLowerCase().includes(searchLower) ||
      String(remap.age).includes(searchLower)
    );
  });

  const columns: GridColumns = [
    { field: 'heightCategory', headerName: t('heightCategory'), flex: 1 },
    { field: 'weightCategory', headerName: t('weightCategory'), flex: 1 },
    { field: 'age', headerName: t('age'), flex: 0.5 },
    { field: 'currentSize', headerName: t('currentSize'), flex: 0.7 },
    { field: 'proposedSize', headerName: t('proposedSize'), flex: 0.7 },
    {
      field: 'actions',
      headerName: t('actions'),
      width: 120,
      renderCell: (params: GridCellParams) => (
        <IconButton
          color="primary"
          onClick={() => handleDeleteRemap(params.row.id as number)}
          disabled={loading}
          size="large"
          title={t('delete')}
        >
          <DeleteIcon />
        </IconButton>
      )
    }
  ];

  return (
    <Container>
      <FormSection>
        <FormSectionTitle>{t('sizeRemapConfiguration')}</FormSectionTitle>
        <FormRow>
          <FormLabel>{t('brand')}</FormLabel>
          <BrandSelector
            selectedBrand={selectedBrand}
            onBrandChanged={handleBrandChange}
            disabled={loading}
          />
        </FormRow>

        <FormRow>
          <FormLabel>{t('productType')}</FormLabel>
          <Box sx={{ width: '100%' }}>
            <Select
              fullWidth
              value={selectedProductType}
              onChange={handleProductTypeChange}
              displayEmpty
              disabled={loading || !selectedBrand}
            >
              <MenuItem value="">{t('chooseAProductType')}</MenuItem>
              {productTypes.map(type => (
                <MenuItem key={type.name} value={type.name}>
                  {type.name}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </FormRow>

        <FormRow>
          <FormLabel>{t('gender')}</FormLabel>
          <Box sx={{ width: '100%' }}>
            <Select
              fullWidth
              value={selectedGender}
              onChange={handleGenderChange}
              disabled={loading || !selectedBrand || !selectedProductType}
            >
              <MenuItem value="male">{t('male')}</MenuItem>
              <MenuItem value="female">{t('female')}</MenuItem>
              <MenuItem value="unisex">{t('unisex')}</MenuItem>
            </Select>
          </Box>
        </FormRow>
      </FormSection>

      <Gap />

      {selectedBrand?.slug && selectedProductType && selectedGender && (
        <>
          <CenteredRow>
            <SearchContainer>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                value={search}
                onChange={handleSearchChange}
                placeholder={t('searchPlaceholder')}
                InputProps={{
                  startAdornment: <SearchIcon fontSize="small" />,
                  endAdornment: (
                    <IconButton
                      title={t('clear')}
                      aria-label="Clear"
                      size="small"
                      style={{ visibility: search ? 'visible' : 'hidden' }}
                      onClick={() => setSearch('')}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )
                }}
              />
            </SearchContainer>
            <Gap />
            <FileInputButton
              buttonText={t('importCsv')}
              onFileSelected={handleImportCsv}
              accept=".csv"
              loading={loading}
              icon={<ImportIcon />}
              disabled={
                !selectedBrand?.slug || !selectedProductType || !selectedGender
              }
            />
            <Gap />
            <Button
              variant="contained"
              color="primary"
              onClick={exportRemaps}
              disabled={loading || sizeRemaps.length === 0}
              startIcon={<ExportIcon />}
            >
              {t('export')}
            </Button>
          </CenteredRow>

          <DataGridFrame>
            <DataGrid
              rows={filteredRemaps}
              columns={columns}
              loading={loading}
              disableColumnMenu
              disableSelectionOnClick
              disableColumnSelector
              isRowSelectable={() => false}
              components={{
                NoRowsOverlay: () => (
                  <Box
                    sx={{
                      display: 'flex',
                      height: '100%',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography>{t('noSizeRemapsFound')}</Typography>
                  </Box>
                )
              }}
              rowsPerPageOptions={[25, 50, 100]}
              initialState={{
                pagination: {
                  pageSize: 25
                }
              }}
              pagination
            />
          </DataGridFrame>
        </>
      )}

      {confirmDelete && (
        <Dialog
          open={!!confirmDelete}
          onClose={() => setConfirmDelete(null)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {t('deleteRemapConfirmation')}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              {t('deleteRemapConfirmationText')}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setConfirmDelete(null)}
              color="primary"
              variant="outlined"
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={confirmDelete.onConfirm}
              color="primary"
              variant="contained"
              autoFocus
            >
              {t('delete')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {notification}
    </Container>
  );
}
